import { createContext, useContext, useState, ReactNode, useEffect } from "react"
import { authService } from "../services/authService"
import api, { api_v2 } from "../services/api"
import { LoginResponse } from "@/types/auth"

interface AuthContextType {
  user: LoginResponse | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<LoginResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user")
      const storedToken = localStorage.getItem("token")

      if (storedUser && storedToken) {
        setUser(JSON.parse(storedUser))
        api.defaults.headers.Authorization = `${storedToken}`
        api_v2.defaults.headers.Authorization = `${storedToken}`
      }
    } catch (error) {
      console.error("Falha ao reidratar autenticação:", error)
      setUser(null)
      localStorage.removeItem("user")
      localStorage.removeItem("token")
      api.defaults.headers.Authorization = null
      api_v2.defaults.headers.Authorization = null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const login = async (email: string, password: string) => {
    const data = await authService.login(email, password)
    const user = data.user
    const token = data.token

    setUser(user)
    localStorage.setItem("user", JSON.stringify(user))
    localStorage.setItem("token", token)
    api.defaults.headers.Authorization = `${token}`
    api_v2.defaults.headers.Authorization = `${token}`
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem("user")
    localStorage.removeItem("token")
    api.defaults.headers.Authorization = null
    api_v2.defaults.headers.Authorization = null
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated: localStorage.getItem("user") !== null,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
